// =============================================
// useImageGeneration — hook for FAL.AI featured image generation
// =============================================

import { useState, useCallback } from "react";
import { generateFeaturedImage, generateBatchImages } from "@/services/imageGeneratorService";
import { useToast } from "@/hooks/use-toast";

interface ImageGenerationProgress {
  current: number;
  total: number;
  nodeId?: string;
}

export function useImageGeneration(mapId?: string) {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<ImageGenerationProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const generateForNode = useCallback(async (nodeId: string) => {
    if (!mapId) return null;
    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: 1, nodeId });

    try {
      const result = await generateFeaturedImage(nodeId, mapId);
      setProgress({ current: 1, total: 1, nodeId });

      if (result) {
        toast({ title: "Obraz wygenerowany", description: "Featured image zapisany." });
      } else {
        toast({ title: "Brak obrazu", description: "FAL.AI nie zwrócił obrazu.", variant: "destructive" });
      }
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(msg);
      toast({ title: "Błąd generowania obrazu", description: msg, variant: "destructive" });
      return null;
    } finally {
      setLoading(false);
      setProgress(null);
    }
  }, [mapId, toast]);

  const generateForMap = useCallback(async (nodeIds: string[]) => {
    if (!mapId || nodeIds.length === 0) return null;
    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: nodeIds.length });

    try {
      const result = await generateBatchImages(nodeIds, mapId);
      setProgress({ current: nodeIds.length, total: nodeIds.length });

      toast({
        title: "Batch obrazów zakończony",
        description: `${result.success} OK, ${result.failed} błędów, ${result.skipped} pominięto`,
      });
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(msg);
      toast({ title: "Błąd batch obrazów", description: msg, variant: "destructive" });
      return null;
    } finally {
      setLoading(false);
      setProgress(null);
    }
  }, [mapId, toast]);

  return { loading, progress, error, generateForNode, generateForMap };
}
